import React from "react";
import ReactDOM from "react-dom";
import { HiX } from "react-icons/hi";

// Full-screen camera scanner for the QR on a till's customer screen, so the
// customer never has to leave the site for their camera app. Uses the
// browser's own BarcodeDetector where there is one (Chrome / Android) and
// falls back to jsQR (Safari, Firefox) — loaded only when the scanner opens.

/** A till QR (dineplatform-pos src/lib/loyaltyLink.ts):
 *  https://www.dineplatform.com/r/<appId>/link?store=…&order=…&c=<check id> */
export function parseTillLink(text: string): { appId: string; url: string; search: string } | null {
  let u: URL;
  try {
    u = new URL(text.trim());
  } catch {
    return null;
  }
  if (!/(^|\.)dineplatform\.com$/i.test(u.hostname)) return null;
  const m = u.pathname.match(/^\/r\/([^/]+)\/link\/?$/);
  if (!m || !u.searchParams.get("c") || !u.searchParams.get("store")) return null;
  return { appId: decodeURIComponent(m[1]), url: u.toString(), search: u.search };
}

type Props = {
  /** Called with each decoded QR; return true to accept it (the scanner then stops). */
  onResult: (text: string) => boolean;
  onClose: () => void;
};

const css = `
.qrs { position: fixed; left: 0; top: 0; right: 0; bottom: 0; z-index: 10000; background: #000; color: #fff; overscroll-behavior: contain; }
.qrs video { position: absolute; left: 0; top: 0; width: 100%; height: 100%; object-fit: cover; }
.qrs-frame {
  position: absolute; left: 50%; top: 46%; width: 64vmin; height: 64vmin; max-width: 300px; max-height: 300px;
  transform: translate(-50%, -50%); border-radius: 22px; box-shadow: 0 0 0 100vmax rgba(0,0,0,.55);
  border: 3px solid rgba(255,255,255,.9);
}
.qrs-top { position: absolute; left: 0; right: 0; top: 0; padding: calc(env(safe-area-inset-top) + 14px) 16px 14px; display: flex; justify-content: space-between; align-items: center; }
.qrs-title { font-size: 17px; font-weight: 800; letter-spacing: -0.2px; }
.qrs-close { width: 42px; height: 42px; border-radius: 50%; border: none; background: rgba(255,255,255,.18); color: #fff; font-size: 22px; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.qrs-bottom { position: absolute; left: 0; right: 0; bottom: 0; padding: 18px 20px calc(env(safe-area-inset-bottom) + 28px); text-align: center; font-size: 14.5px; line-height: 1.5; }
.qrs-hint { opacity: .9; }
.qrs-wrong { display: inline-block; margin-top: 10px; background: rgba(229,72,77,.9); border-radius: 10px; padding: 7px 12px; font-size: 13.5px; font-weight: 600; }
.qrs-msg { position: absolute; left: 0; right: 0; top: 50%; transform: translateY(-50%); padding: 0 28px; text-align: center; font-size: 15px; line-height: 1.55; }
.qrs-msg b { display: block; font-size: 18px; margin-bottom: 8px; }
`;

const QrScanner = ({ onResult, onClose }: Props) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [problem, setProblem] = React.useState<"" | "blocked" | "unavailable">("");
  const [wrong, setWrong] = React.useState(false);
  const onResultRef = React.useRef(onResult);
  onResultRef.current = onResult;

  React.useEffect(() => {
    let stopped = false;
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let wrongTimer: number | undefined;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true } as any) as CanvasRenderingContext2D | null;

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setProblem("unavailable");
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
      } catch (e: any) {
        if (!stopped) setProblem(e?.name === "NotAllowedError" || e?.name === "SecurityError" ? "blocked" : "unavailable");
        return;
      }
      if (stopped) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      try {
        await video.play();
      } catch {
        /* autoplay is allowed for muted inline video; frames still arrive */
      }

      let detect: (v: HTMLVideoElement) => Promise<string | null>;
      const BD = (window as any).BarcodeDetector;
      let native: any = null;
      if (BD) {
        try {
          const formats: string[] = typeof BD.getSupportedFormats === "function" ? await BD.getSupportedFormats() : ["qr_code"];
          if (formats.includes("qr_code")) native = new BD({ formats: ["qr_code"] });
        } catch {
          native = null;
        }
      }
      if (native) {
        detect = async (v) => {
          const codes = await native.detect(v);
          return codes && codes[0] ? String(codes[0].rawValue || "") : null;
        };
      } else {
        const jsQR = (await import("jsqr")).default;
        detect = async (v) => {
          if (!ctx || !v.videoWidth) return null;
          // Downscale: full-resolution frames make jsQR slow on older phones.
          const scale = Math.min(1, 720 / Math.max(v.videoWidth, v.videoHeight));
          const w = Math.round(v.videoWidth * scale);
          const h = Math.round(v.videoHeight * scale);
          if (canvas.width !== w) canvas.width = w;
          if (canvas.height !== h) canvas.height = h;
          ctx.drawImage(v, 0, 0, w, h);
          const img = ctx.getImageData(0, 0, w, h);
          const code = jsQR(img.data, w, h, { inversionAttempts: "dontInvert" });
          return code ? code.data : null;
        };
      }

      const tick = async () => {
        if (stopped) return;
        const v = videoRef.current;
        if (v && v.readyState >= 2) {
          try {
            const text = await detect(v);
            if (stopped) return;
            if (text) {
              if (onResultRef.current(text)) {
                stopped = true;
                return;
              }
              setWrong(true);
              window.clearTimeout(wrongTimer);
              wrongTimer = window.setTimeout(() => setWrong(false), 2500);
            }
          } catch {
            /* a bad frame — try the next one */
          }
        }
        timer = window.setTimeout(tick, 180);
      };
      tick();
    };
    start();

    return () => {
      stopped = true;
      window.clearTimeout(timer);
      window.clearTimeout(wrongTimer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return ReactDOM.createPortal(
    <div className="qrs" role="dialog" aria-label="Scan the QR code on the till">
      <style>{css}</style>
      {problem ? (
        <div className="qrs-msg">
          <b>{problem === "blocked" ? "Camera access is off" : "Camera not available"}</b>
          {problem === "blocked"
            ? "Allow camera access for this site in your browser settings, or scan the code with your phone's camera app instead."
            : "Scan the code on the till's screen with your phone's camera app instead."}
        </div>
      ) : (
        <>
          <video ref={videoRef} playsInline muted autoPlay />
          <div className="qrs-frame" />
        </>
      )}
      <div className="qrs-top">
        <span className="qrs-title">Scan the till's QR code</span>
        <button className="qrs-close" onClick={onClose} aria-label="Close scanner">
          <HiX />
        </button>
      </div>
      {!problem && (
        <div className="qrs-bottom">
          <div className="qrs-hint">Point your camera at the QR code on the screen facing you at the till.</div>
          {wrong && <div className="qrs-wrong">That isn't a till code — try the one on the till's screen</div>}
        </div>
      )}
    </div>,
    document.body
  );
};

export default QrScanner;
